import { computed, type ComputedRef, type Ref, ref, watch, type WritableComputedRef } from "vue";
import { useCurrentInstance } from "./current-instance";

const useTwoWayBinding = <T>(value: ComputedRef<T>, event: string) => {
	const instance = useCurrentInstance();

	const castToNumber = typeof value.value === "number";

	const lazyValue = ref(value.value);

	watch(value, () => {
		lazyValue.value = value.value as any;
	});

	return computed({
		get: () => lazyValue.value,
		set: (value) => {
			lazyValue.value = value;
			instance.emit(event, castToNumber ? Number(value) : value);
		},
	});
};

type PropertyName<T> = {
	[K in keyof T]: K;
}[keyof T] &
	string;

export function useModelValue<T extends { modelValue?: unknown }>(
	props: T,
	defaultValue?: T["modelValue"]
): WritableComputedRef<T["modelValue"]>;

export function useModelValue<T, P extends PropertyName<Required<T>>>(
	props: T,
	propName: P,
	defaultValue?: T[P]
): WritableComputedRef<T[P]>;

export function useModelValue<
	T extends { modelValue?: unknown },
	P extends PropertyName<Required<T>>,
>(props: T, propName?: P, defaultValue?: T[P]) {
	const name = propName || "modelValue";
	const value = computed(() => props[name] ?? defaultValue);
	return useTwoWayBinding<T[P]>(value as any, `update:${name}`);
}

type RefObjectSyncOptions = {
	nullAsUndefined?: boolean;
};

export const useRefObjectSync = <T, P extends PropertyName<Required<T>>>(
	refObject: Ref<T>,
	key: P,
	options: RefObjectSyncOptions = {}
) => {
	return computed({
		get: () => {
			const val = refObject.value[key];
			if (options.nullAsUndefined) {
				return val || undefined;
			}

			return val;
		},
		set: (val) => {
			if (val === undefined) return;

			const newObject = { ...refObject.value };
			newObject[key] = val;

			// Force ref refresh
			refObject.value = newObject;
		},
	}) as Ref<T[P]>;
};
